// authSlice.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

interface AuthState {
  email: string;
  password: string;
  userID: string;
  isEmailVerified: boolean;
  selectedDocumentName: string;
  selectedCountryName: string;
  selectedRegionName: string;
  driverLicenseFrontendPic: string; // New attribute
  driverLicenseBackendPic: string;
  identityCardFrontendPic: string;
  identityCardBackendPic: string;
  passportFrontendPic: string;
  passportBackendPic: string;
  otherIdentityFrontendPic: string;
  otherIdentityBackendPic: string;
  userVideo: string;
  firstName: string;
  lastName: string;
  entityId: string;
  selectedDocumentNumberIdentityCard: string;
  selectedDocumentNumberDriverLicense: string;
  selectedDocumentNumberPassport: string;
  selectedDocumentNumberOtherIdentity: string;
  selectedDocumentNumberOtherDocument: string;
  dateofBirth: string;
  registrationDob: string;
  phoneNo: string;
  questionnaireScreen1: string[];
  questionnaireScreen2: string[];
  questionnaireScreen3: string;
  questionnaireScreen4: string;
  questionnaireScreen5: string;
  questionnaireScreen6: string[];
  wizardPageNumber: number;
  appName: string;
  wizardStepValidation: {
    [key: number]: boolean;
  };
  kycToken: string;
  streetAddress: string;
  city: string;
  postalCode: string;
  middleName: string;
  invitationId: string;
  entityName: string;
}

const initialState: AuthState = {
  email: "",
  password: "",
  userID: "",
  isEmailVerified: false,
  selectedDocumentName: "",
  selectedCountryName: "",
  selectedRegionName: "",
  driverLicenseFrontendPic: "",
  driverLicenseBackendPic: "",
  identityCardFrontendPic: "",
  identityCardBackendPic: "",
  passportFrontendPic: "",
  passportBackendPic: "",
  otherIdentityFrontendPic: "",
  otherIdentityBackendPic: "",
  userVideo: "",
  firstName: "",
  lastName: "",
  entityId: "",
  selectedDocumentNumberIdentityCard: "",
  selectedDocumentNumberDriverLicense: "",
  selectedDocumentNumberPassport: "",
  selectedDocumentNumberOtherIdentity: "",
  selectedDocumentNumberOtherDocument: "",
  dateofBirth: "",
  registrationDob: "",
  phoneNo: "",
  questionnaireScreen1: [],
  questionnaireScreen2: [],
  questionnaireScreen3: "",
  questionnaireScreen4: "",
  questionnaireScreen5: "",
  questionnaireScreen6: [],
  wizardPageNumber: 1,
  appName: "",
  wizardStepValidation: {
    1: true,
    2: true,
    3: true,
    4: true,
    5: true,
    6: true,
    7: true,
    8: true,
    9: true,
    10: true,
    11: true,
    12: true,
    13: true,
  },
  kycToken: "",
  streetAddress: "",
  city: "",
  postalCode: "",
  middleName: "",
  invitationId: "",
  entityName: "",
};

// Create your auth slice
const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setEmail: (state, action: PayloadAction<string>) => {
      state.email = action.payload;
    },
    setPassword: (state, action: PayloadAction<string>) => {
      state.password = action.payload;
    },
    setUserID: (state, action: PayloadAction<string>) => {
      state.userID = action.payload;
    },
    setSelectedDocument: (state, action: PayloadAction<string>) => {
      state.selectedDocumentName = action.payload;
    },
    setSelectedCountry: (state, action: PayloadAction<string>) => {
      state.selectedCountryName = action.payload;
    },
    setSelectedRegion: (state, action: PayloadAction<string>) => {
      state.selectedRegionName = action.payload;
    },
    setEmailVerify: (state, action: PayloadAction<boolean>) => {
      state.isEmailVerified = action.payload;
    },
    setdriverLicenseFrontendPic: (state, action: PayloadAction<string>) => {
      state.driverLicenseFrontendPic = action.payload;
    },
    setdriverLicenseBackendPic: (state, action: PayloadAction<string>) => {
      state.driverLicenseBackendPic = action.payload;
    },
    setidentityCardFrontendPic: (state, action: PayloadAction<string>) => {
      state.identityCardFrontendPic = action.payload;
    },
    setidentityCardBackendPic: (state, action: PayloadAction<string>) => {
      state.identityCardBackendPic = action.payload;
    },
    setpassportFrontendPic: (state, action: PayloadAction<string>) => {
      state.passportFrontendPic = action.payload;
    },
    setpassportBackendPic: (state, action: PayloadAction<string>) => {
      state.passportBackendPic = action.payload;
    },
    setotherIdentityFrontendPic: (state, action: PayloadAction<string>) => {
      state.otherIdentityFrontendPic = action.payload;
    },
    setotherIdentityBackendPic: (state, action: PayloadAction<string>) => {
      state.otherIdentityBackendPic = action.payload;
    },
    setuserVideo: (state, action: PayloadAction<string>) => {
      state.userVideo = action.payload;
    },
    setwizardPageNumber: (state, action: PayloadAction<number>) => {
      state.wizardPageNumber = action.payload;
    },
    setWizardStepValidation: (
      state,
      action: PayloadAction<{ step: number; isValid: boolean }>
    ) => {
      const { step, isValid } = action.payload;
      state.wizardStepValidation[step] = isValid;
    },
    resetWizard: (state) => {
      state.wizardPageNumber = 1;
      state.wizardStepValidation = {
        1: true,
        2: true,
        3: true,
        4: true,
        5: true,
        6: true,
        7: true,
        8: true,
        9: true,
        10: true,
        11: true,
        12: true,
        13: true,
      };
    },
    setfirstName: (state, action: PayloadAction<string>) => {
      state.firstName = action.payload;
    },
    setlastName: (state, action: PayloadAction<string>) => {
      state.lastName = action.payload;
    },
    setEntityId: (state, action: PayloadAction<string>) => {
      state.entityId = action.payload;
    },
    setdateOfBirth: (state, action: PayloadAction<string>) => {
      state.dateofBirth = action.payload;
    },
    setRegistrationDob: (state, action: PayloadAction<string>) => {
      state.registrationDob = action.payload;
    },
    setdriverLicenseNumber: (state, action: PayloadAction<string>) => {
      state.selectedDocumentNumberDriverLicense = action.payload;
    },
    setidentityCardNumber: (state, action: PayloadAction<string>) => {
      state.selectedDocumentNumberIdentityCard = action.payload;
    },
    setpassportNumber: (state, action: PayloadAction<string>) => {
      state.selectedDocumentNumberPassport = action.payload;
    },
    setotherIdentityNumber: (state, action: PayloadAction<string>) => {
      state.selectedDocumentNumberOtherIdentity = action.payload;
    },
    setotherDocumentNumber: (state, action: PayloadAction<string>) => {
      state.selectedDocumentNumberOtherDocument = action.payload;
    },
    setphoneNo: (state, action: PayloadAction<string>) => {
      state.phoneNo = action.payload;
    },

    setQuestionnaireScreen1: (state, action: PayloadAction<string[]>) => {
      state.questionnaireScreen1 = action.payload;
    },
    setQuestionnaireScreen2: (state, action: PayloadAction<string[]>) => {
      state.questionnaireScreen2 = action.payload;
    },
    setQuestionnaireScreen3: (state, action: PayloadAction<string>) => {
      state.questionnaireScreen3 = action.payload;
    },
    setQuestionnaireScreen4: (state, action: PayloadAction<string>) => {
      state.questionnaireScreen4 = action.payload;
    },
    setQuestionnaireScreen5: (state, action: PayloadAction<string>) => {
      state.questionnaireScreen5 = action.payload;
    },
    setQuestionnaireScreen6: (state, action: PayloadAction<string[]>) => {
      state.questionnaireScreen6 = action.payload;
    },
    setAppName: (state, action: PayloadAction<string>) => {
      state.appName = action.payload;
    },
    setKycToken: (state, action: PayloadAction<string>) => {
      state.kycToken = action.payload;
    },
    setstreetAddress: (state, action: PayloadAction<string>) => {
      state.streetAddress = action.payload;
    },
    setcity: (state, action: PayloadAction<string>) => {
      state.city = action.payload;
    },
    setpostalCode: (state, action: PayloadAction<string>) => {
      state.postalCode = action.payload;
    },
    setmiddleName: (state, action: PayloadAction<string>) => {
      state.middleName = action.payload;
    },
    setInvitationId: (state, action: PayloadAction<string>) => {
      state.invitationId = action.payload;
    },
    setEntityName: (state, action: PayloadAction<string>) => {
      state.entityName = action.payload;
    },
  },
});

export const resetAllFields = () => (dispatch: any) => {
  dispatch(setwizardPageNumber(1));
  dispatch(resetWizard());
  dispatch(setEmail(""));
  dispatch(setPassword(""));
  dispatch(setEmailVerify(false));
  dispatch(setSelectedDocument(""));
  dispatch(setSelectedCountry(""));
  dispatch(setdriverLicenseFrontendPic(""));
  dispatch(setUserID(""));
  dispatch(setdriverLicenseBackendPic(""));
  dispatch(setidentityCardFrontendPic(""));
  dispatch(setidentityCardBackendPic(""));
  dispatch(setpassportFrontendPic(""));
  dispatch(setpassportBackendPic(""));
  dispatch(setotherIdentityFrontendPic(""));
  dispatch(setotherIdentityBackendPic(""));
  dispatch(setuserVideo(""));
  dispatch(setQuestionnaireScreen1([]));
  dispatch(setQuestionnaireScreen2([]));
  dispatch(setQuestionnaireScreen3(""));
  dispatch(setQuestionnaireScreen4(""));
  dispatch(setQuestionnaireScreen5(""));
  dispatch(setQuestionnaireScreen6([]));
  dispatch(setKycToken(""));
  dispatch(setInvitationId(""));
  dispatch(setEntityId(""));
  dispatch(setEntityName(""));
  dispatch(setfirstName(""));
  dispatch(setlastName(""));
  dispatch(setmiddleName(""));
  dispatch(setdateOfBirth(""));
  dispatch(setRegistrationDob(""));
  dispatch(setphoneNo(""));
  dispatch(setstreetAddress(""));
  dispatch(setcity(""));
  dispatch(setpostalCode(""));
};

// Theme slice
const initialTheme = localStorage.getItem("theme") || "light";

const themeSlice = createSlice({
  name: "theme",
  initialState: { theme: initialTheme },
  reducers: {
    toggleTheme: (state) => {
      state.theme = state.theme === "light" ? "dark" : "light";
      localStorage.setItem("theme", state.theme);
    },
    setTheme: (state, action: PayloadAction<string>) => {
      state.theme = action.payload;
      localStorage.setItem("theme", state.theme);
    },
  },
});

export const { toggleTheme, setTheme } = themeSlice.actions;

// Export reducers
export const themeReducer = themeSlice.reducer;

export const {
  setWizardStepValidation,
  resetWizard,
  setEmail,
  setPassword,
  setEmailVerify,
  setSelectedDocument,
  setSelectedCountry,
  setSelectedRegion,
  setdriverLicenseFrontendPic,
  setUserID,
  setdriverLicenseBackendPic,
  setidentityCardFrontendPic,
  setidentityCardBackendPic,
  setpassportFrontendPic,
  setpassportBackendPic,
  setotherIdentityFrontendPic,
  setotherIdentityBackendPic,
  setuserVideo,
  setfirstName,
  setlastName,
  setEntityId,
  setdateOfBirth,
  setRegistrationDob,
  setdriverLicenseNumber,
  setidentityCardNumber,
  setpassportNumber,
  setotherIdentityNumber,
  setotherDocumentNumber,
  setphoneNo,
  setQuestionnaireScreen1,
  setQuestionnaireScreen2,
  setQuestionnaireScreen3,
  setQuestionnaireScreen4,
  setQuestionnaireScreen5,
  setQuestionnaireScreen6,
  setwizardPageNumber,
  setAppName,
  setKycToken,
  setstreetAddress,
  setcity,
  setpostalCode,
  setmiddleName,
  setInvitationId,
  setEntityName,
} = authSlice.actions;
export default authSlice.reducer;
